import {
    FaCheckCircle,
    FaExclamationCircle,
    FaExclamationTriangle,
    FaInfoCircle,
    FaQuestionCircle,
    FaTimesCircle
} from 'react-icons/fa';
import { IoSyncCircle } from 'react-icons/io5';
import { Button } from 'reakit/Button';
import {
    Tooltip,
    TooltipArrow,
    TooltipReference,
    useTooltipState
} from 'reakit/Tooltip';

import styles from './StatusIcon.module.less';

const config = {
    completed: props => <FaCheckCircle {...props} />,
    success: props => <FaCheckCircle {...props} />,
    warning: props => <FaExclamationTriangle {...props} />,
    pending: props => <IoSyncCircle {...props} />,
    syncing: props => <IoSyncCircle {...props} />,
    info: props => <FaInfoCircle {...props} />,
    danger: props => <FaExclamationCircle {...props} />,
    failure: props => <FaTimesCircle {...props} />,
    error: props => <FaTimesCircle {...props} />
};

/**
 * Render method for the class. All other props are pass-thru.
 * @param {string} status kind of icon to show. If excluded, a (?)-esque icon is shown
 */
const FinalIcon = props => {
    const { status = '' } = props;

    // give a placeholder icon if an invalid status was passed
    if (!Object.keys(config).includes(status)) {
        console.error(
            `invalid status passed to StatusIcon - try again with ${Object.keys(
                config
            ).toString()}`
        );
        return (
            <FaQuestionCircle
                role="presentation"
                className={styles['status-icon-help']}
            />
        );
    }

    // return icon based on config
    return <>{config[status](props)}</>;
};

/**
 * A fixed set of icons designed to visually aid status representations. Uses
 * Reakit's Tooltip to provide contextual info as needed.
 *
 * @example
 * ```jsx
 * <StatusIcon status="success" />
 * ```
 * @param {string} [className] any extra class name(s) that should be appended
 * @param {string} status defines kind of icon to show
 * @param {object} [style] CSS style definitions
 * @param {boolean} [useTooltip=false] determines if a Tooltip should be included. If true, tooltipText is required.
 * @param {string} [tooltipText]
 * @param {string} [tooltipPlacement=auto] passed through to determine location of the tooltip. See https://reakit.io/docs/popover/#usepopoverstate
 * @param {string} [id=hvui-status-icon] HTML id attribute. Used to compose id for Tooltip.
 */
const StatusIcon = props => {
    const {
        className = '',
        id = 'hvui-status-icon',
        status = '',
        style,
        tooltipMaxWidth,
        tooltipPlacement = 'auto',
        tooltipText = '',
        useTooltip
    } = props;

    const classNameBase =
        status && Object.keys(config).includes(status)
            ? `${styles['status-icon-' + status]} ${styles[className]}`
            : '';
    const tooltip = useTooltipState({
        baseId: `${id}-tooltip`,
        placement: tooltipPlacement
    });

    if (useTooltip) {
        return (
            <>
                <TooltipReference
                    {...tooltip}
                    aria-label={status}
                    as={Button}
                    className={styles['hvui-status-tooltip-button']}
                >
                    <FinalIcon
                        id={id}
                        role="presentation"
                        className={classNameBase}
                        status={status}
                        style={style}
                    />
                </TooltipReference>
                <Tooltip
                    {...tooltip}
                    className={styles['hvui-status-tooltip']}
                    style={{
                        maxWidth: tooltipMaxWidth
                            ? `${tooltipMaxWidth}px`
                            : 'initial'
                    }}
                >
                    <TooltipArrow {...tooltip} />
                    {tooltipText}
                </Tooltip>
            </>
        );
    } else {
        return (
            <FinalIcon
                id={id}
                role="presentation"
                className={classNameBase}
                status={status}
                style={style}
            />
        );
    }
};

export default StatusIcon;
